
import { defineComponent, ref, computed, watch, onMounted, getCurrentInstance } from "vue-demi";
import { createDateRangeRuleWithinLast } from "@/utils/rules";
import { IIELTSForm } from "@/models/IELTS";
import { TInputRule } from "@nationbetter/nb-components/src/models/Input";

const FORM_MODEL: IIELTSForm = {
  date: "",
  family_name: "",
  first_name: "",
  listening: "",
  reading: "",
  speaking: "",
  ukvi_number: "",
  writing: "",
};

const MASKS = {
  score: "N.N",
};

export default defineComponent({
  name: "FormIELTS",

  props: {
    loading: { type: Boolean, default: false },
    details: { type: Object as () => IIELTSForm, required: true },
  },

  emits: ["update"],

  setup(props, { emit }) {
    const instance = getCurrentInstance()?.proxy;
    const form = ref<IIELTSForm>({ ...FORM_MODEL });

    const requiredRules = [(v: string) => v.length || String(instance?.$t("errors.required"))];

    const scoreMask = MASKS.score;

    const scoreRules: TInputRule[] = [
      (v) => !!v.length || String(instance?.$t("errors.required")),
      (v) => Number(v) >= 5.5 || String(instance?.$t("errors.score")),
      (v) => {
        const str = String(v);
        return str[str.length - 1] !== "." || String(instance?.$t("errors.lastDotChar"));
      },
    ];

    const dateRules = [
      createDateRangeRuleWithinLast({ years: 2 }, String(instance?.$t("errors.date"))),
      (v: string) => !!v?.length || String(instance?.$t("errors.required")),
    ];

    const isFormValid = computed(() => {
      const {
        family_name,
        first_name,
        ukvi_number,
        listening,
        reading,
        writing,
        speaking,
        date,
      } = form.value;

      const scores = [listening, reading, writing, speaking];
      const fields = [family_name, first_name, ukvi_number, ...scores, date];

      const allFieldsFilled = fields.every((v) => v !== "");
      const allScoresValid = scores.every((v) => Number(v) >= 5.5);
      const isDateValid = dateRules.every((rule) => rule(date) === true);

      return allFieldsFilled && allScoresValid && isDateValid;
    });

    const setCosForm = (data: IIELTSForm) => {
      Object.assign(form.value, { ...data });
    };

    onMounted(() => {
      setCosForm(props.details);

      emit("update", {
        form: form.value,
        valid: isFormValid.value,
      });
    });

    watch(
      () => form.value,
      () => {
        emit("update", {
          form: form.value,
          valid: isFormValid.value,
        });
      },
      { deep: true, immediate: true }
    );

    return {
      form,
      requiredRules,
      scoreRules,
      scoreMask,
      dateRules,
      isFormValid,
    };
  },
});
